import React, { useState, useRef, useCallback } from "react";
import "./style.scss";

export type AreaSliderValue = [number, number];

const padding = 20;

export const AreaSlider = ({
  initialValue = [0, 0],
  onValueChange,
  xLabel = "X",
  yLabel = "Y",
  size = 300,
  gradient = "linear-gradient(215deg, var(--fl-color-secondary) 0.06%, var(--fl-color-primary) 79.05%, var(--fl-color-primary) 100%)",
  gradientBottom = "#e64813",
  gradientTop = "#49b044",
}: {
  initialValue?: AreaSliderValue;
  onValueChange?: (position: AreaSliderValue) => void;
  xLabel?: string;
  yLabel?: string;
  size?: number;
  gradient?: string;
  gradientBottom?: string;
  gradientTop?: string;
}) => {
  const [position, setPosition] = useState<AreaSliderValue>(initialValue);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const updatePosition = useCallback(
    (clientX: number, clientY: number) => {
      if (!containerRef.current) return;

      // Get the bounding rectangle of the container
      const rect = containerRef.current.getBoundingClientRect();
      // Calculate mouse position relative to the container
      const x = clientX - rect.left;
      const y = clientY - rect.top;

      // Convert to normalized coordinates (-1 to 1)
      // Convert to normalized coordinates (0 to 1)
      const normalizedX = Math.max(0, Math.min(1, x / size));
      // Y axis: 0 at bottom, 1 at top
      const normalizedY = Math.max(0, Math.min(1, 1 - y / size));

      const newPosition: AreaSliderValue = [normalizedX, normalizedY];
      setPosition(newPosition);
      if (onValueChange) {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
          onValueChange(newPosition);
        }, 300);
      }
    },
    [size, onValueChange]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      updatePosition(e.clientX, e.clientY);

      const handleMouseMove = (e: MouseEvent) => {
        updatePosition(e.clientX, e.clientY);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [updatePosition]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault(); // Prevent scrolling on touch start
      setIsDragging(true);
      const touch = e.touches[0];
      updatePosition(touch.clientX, touch.clientY);

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault(); // Prevent scrolling on touch move
        const touch = e.touches[0];
        updatePosition(touch.clientX, touch.clientY);
      };

      const handleTouchEnd = () => {
        setIsDragging(false);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };

      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
    },
    [updatePosition]
  );

  const pixelX = position[0] * size; // X: 0 (left) to size (right)
  const pixelY = (1 - position[1]) * size; // Y: 0 (bottom) to size (top)

  return (
    <div className="tg_area-slider">
      <div
        className="area-slider-wrapper"
        style={{
          width: size + padding,
          height: size + padding,
          paddingTop: padding,
          paddingLeft: padding,
        }}
      >
        <div className="y-label">{yLabel}</div>
        <div
          ref={containerRef}
          className="area-slider-container"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          style={{
            width: size,
            height: size,
          }}
        >
          <div
            className="area-slider-background"
            style={{
              background:
                gradient ||
                `linear-gradient(45deg, ${gradientBottom} 20%, var(--cube-fallback-color) 45%, ${gradientTop} 65%)`,
            }}
          />
          <div
            className="grid-line horizontal"
            style={{ top: `${pixelY}px` }}
          />
          <div className="grid-line vertical" style={{ left: `${pixelX}px` }} />
          <div
            className={`dragger ${isDragging ? "dragging" : ""}`}
            style={{
              left: pixelX,
              top: pixelY,
            }}
          />
        </div>
        <div className="x-label">{xLabel}</div>
      </div>
    </div>
  );
};
